import {
  Component,
  input,
  computed,
  output,
  signal,
  ElementRef,
  effect,
  OnDestroy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { IconComponent } from '../icon/icon.component';

type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'danger'
  | 'ghost'
  | 'success'
  | 'warning';
type ButtonSize = 'small' | 'medium' | 'large';

export interface DropdownItem {
  label: string;
  icon?: string;
  value: any;
  disabled?: boolean;
}

@Component({
  selector: 'htx-button',
  standalone: true,
  imports: [CommonModule, IconComponent],
  templateUrl: './button.component.html',
  styleUrl: './button.component.scss',
  host: {
    '[class.full-width]': 'fullWidth()'
  }
})
export class ButtonComponent implements OnDestroy {
  /**
   * Alignment of the dropdown menu: 'left' or 'right'
   */
  dropdownAlign = input<'left' | 'right'>('left');
  /**
   * Delay in ms before closing dropdown on mouseleave
   */
  dropdownCloseDelay = input<number>(300);

  private closeTimeout: any = null;
  /**
   * The variant style of the button
   */
  variant = input<ButtonVariant>('primary');

  /**
   * The size of the button
   */
  size = input<ButtonSize>('medium');

  /**
   * Whether the button is disabled
   */
  disabled = input<boolean>(false);

  /**
   * Icon to display (Material Icon name)
   */
  icon = input<string | null>(null);

  /**
   * Position of the icon ('start' or 'end')
   */
  iconPosition = input<'start' | 'end'>('start');

  /**
   * Whether to use a flat button style
   */
  flat = input<boolean>(true);

  /**
   * Whether the button should be full width
   */
  fullWidth = input<boolean>(false);

  /**
   * Whether to use icon-only variant (small square button for actions)
   */
  iconOnly = input<boolean>(false);

  /**
   * Optional tooltip text to display on hover
   */
  tooltip = input<string | null>(null);

  /**
   * Dropdown items (if provided, button becomes a dropdown)
   */
  dropdownItems = input<DropdownItem[]>([]);

  /**
   * Icon to show for dropdown (default: arrow_drop_down)
   */
  dropdownIcon = input<string>('arrow_drop_down');

  /**
   * When true, the dropdown opens on mouseenter instead of click
   */
  openOnHover = input<boolean>(false);

  /**
   * Optional header text shown at the top of the dropdown (non-clickable)
   */
  dropdownHeader = input<string | null>(null);

  /**
   * Click event emitter
   */
  buttonClick = output<MouseEvent>();

  /**
   * Dropdown item selected event
   */
  dropdownItemSelected = output<DropdownItem>();

  /**
   * Whether the dropdown is open
   */
  isDropdownOpen = signal(false);

  /**
   * Whether the tooltip is currently visible
   */
  isTooltipVisible = signal(false);

  /**
   * Tooltip position (top/left for position: fixed)
   */
  tooltipPos = signal<{ top: string; left: string }>({ top: '0px', left: '0px' });

  onTooltipShow(event: MouseEvent): void {
    if (!this.tooltip()) return;
    const el = event.currentTarget as HTMLElement;
    const rect = el.getBoundingClientRect();
    this.tooltipPos.set({
      top: `${rect.top - 8}px`,
      left: `${rect.left + rect.width / 2}px`
    });
    this.isTooltipVisible.set(true);
  }

  onTooltipHide(): void {
    this.isTooltipVisible.set(false);
  }

  /**
   * Whether this button has a dropdown
   */
  hasDropdown = computed(() => this.dropdownItems().length > 0);

  private isListenerAttached = false;
  private boundOnDocumentClick!: (event: MouseEvent) => void;

  constructor(private elementRef: ElementRef) {
    this.boundOnDocumentClick = this.onDocumentClick.bind(this);

    // Set up event listener reactively when dropdown items are provided
    effect(() => {
      const hasDropdown = this.hasDropdown();
      if (hasDropdown && !this.isListenerAttached) {
        document.addEventListener('click', this.boundOnDocumentClick);
        this.isListenerAttached = true;
      } else if (!hasDropdown && this.isListenerAttached) {
        document.removeEventListener('click', this.boundOnDocumentClick);
        this.isListenerAttached = false;
      }
    });
  }

  getVariantClass() {
    return `btn--${this.variant()}`;
  }

  getSizeClass() {
    return `btn--${this.size()}`;
  }

  onClick(event: MouseEvent): void {
    if (this.hasDropdown() && !this.openOnHover()) {
      event.stopPropagation();
      this.toggleDropdown();
    } else if (!this.hasDropdown()) {
      this.buttonClick.emit(event);
    }
  }

  onMouseEnter(): void {
    if (this.hasDropdown() && this.openOnHover() && !this.disabled()) {
      if (this.closeTimeout) {
        clearTimeout(this.closeTimeout);
        this.closeTimeout = null;
      }
      this.isDropdownOpen.set(true);
    }
  }

  onMouseLeave(): void {
    if (this.openOnHover()) {
      if (this.closeTimeout) {
        clearTimeout(this.closeTimeout);
      }
      this.closeTimeout = setTimeout(() => {
        this.isDropdownOpen.set(false);
        this.closeTimeout = null;
      }, this.dropdownCloseDelay());
    }
  }

  toggleDropdown(): void {
    if (!this.disabled()) {
      this.isDropdownOpen.set(!this.isDropdownOpen());
    }
  }

  selectDropdownItem(item: DropdownItem): void {
    if (!item.disabled) {
      this.dropdownItemSelected.emit(item);
      this.isDropdownOpen.set(false);
    }
  }

  onDocumentClick = (event: MouseEvent): void => {
    const clickedInside = this.elementRef.nativeElement.contains(event.target);
    if (!clickedInside && this.isDropdownOpen()) {
      this.isDropdownOpen.set(false);
    }
  };

  ngOnDestroy(): void {
    if (this.isListenerAttached) {
      document.removeEventListener('click', this.boundOnDocumentClick);
    }
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
    }
  }
}
